import * as React from 'react';
import { remark } from 'remark';
import html from 'remark-html';
import { defaultGetServerSideProps } from 'utils/SSR/defaultServerSideProps';
import Head from 'next/head';
import { DefaultPageProps } from 'types';
import api from 'api/api';
import ReferralContainer from 'containers/Referral';
import ReferralPageProvider from 'contexts/referral/store';
import { PublicReferralCampaign } from 'model/referralCampaign';
import { getCreditCardBySlug } from 'utils/contentful/poinzCards';
import { CreditCard } from 'model';
import { getHeaderTitle } from 'utils/string';
import Error from 'next/error';
import { noIndex } from 'utils/SSR/hidePageOnProduction';

type ReferralCodePageProps = DefaultPageProps & {
  referralCampaign: PublicReferralCampaign;
  creditCard: CreditCard;
  errorCode: any;
  errorTitle?: string;
};

const ReferralCodePage: React.FC<ReferralCodePageProps> = ({
  referralCampaign,
  creditCard,
  errorCode,
  errorTitle
}) => {
  const { image } = creditCard?.meta || {};

  if (errorCode && errorTitle) {
    return <Error statusCode={errorCode} title={errorTitle} />;
  }

  return (
    <div className="ReferralPage w-full">
      <ReferralPageProvider referralCampaign={referralCampaign}>
        <Head>
          <title>{getHeaderTitle(referralCampaign?.recipientName)}</title>
          <meta property="og:title" content={referralCampaign?.recipientName} />
          <meta property="og:image" content={image?.url} key="image" />
          {noIndex()}
        </Head>
        <ReferralContainer />
      </ReferralPageProvider>
    </div>
  );
};

export const getServerSideProps = async (context: any) => {
  const { props } = await defaultGetServerSideProps(context);
  const { params, preview } = context;

  let referralCampaign;
  let creditCard;

  try {
    referralCampaign = await api.referral.campaign.getPublicCampaign({ code: params.code });
    creditCard = await getCreditCardBySlug('referral', preview, props.lang);

    if (!referralCampaign || !creditCard) {
      return {
        notFound: true
      };
    }
  } catch (e: any) {
    if (e.status === 404) {
      return {
        notFound: true
      };
    }
    console.error('Error fetching public campaign data: ', e);
    return { props: { errorCode: 500, errorTitle: e.message } };
  }

  const terms = await remark()
    .use(html)
    .process(referralCampaign?.terms || '');
  const termsHtml = terms.toString();

  return {
    props: {
      ...props,
      referralCampaign: referralCampaign ? { ...referralCampaign, terms: termsHtml } : null,
      creditCard: creditCard || null
    }
  };
};

export default ReferralCodePage;
